import React from "react";
import { useLocation } from "react-router-dom";
import { useModuleConfig } from "../hooks/useModuleConfig";
import { ModuleConfigProvider } from "../contexts/ModuleConfigContext";
import { SharedDashboardProvider } from "@variphigen/shared-dashboards";
import { loadSharedDashboardConfig } from "../runtime/loadSharedDashboardConfig";
import { AuthTransitionScreen } from "../Components/AuthTransitionScreen";
import { useAuthSession, getAuthCallbackPath } from "../auth/session";
import { hasAppLoggedOutFlag } from "../auth/logout";
import { readCachedSessionUser } from "../auth/session/authSession.storage";
import { getToken } from "../services/storageService";
import App from "../App";

interface ModuleConfigBootstrapProps {
  applicationId: string;
}

function useIsAuthCallbackRoute(): boolean {
  const { pathname } = useLocation();
  const callbackPath = getAuthCallbackPath();
  return pathname === callbackPath || pathname.endsWith(callbackPath);
}

export const ModuleConfigBootstrap: React.FC<ModuleConfigBootstrapProps> = ({
  applicationId,
}) => {
  const onAuthCallback = useIsAuthCallbackRoute();
  const { data: sessionUser } = useAuthSession();
  const cachedSession = readCachedSessionUser();
  const session = sessionUser ?? cachedSession;
  const token = getToken();

  const shouldFetchModuleConfig =
    Boolean(applicationId) &&
    Boolean(token) &&
    Boolean(session) &&
    !hasAppLoggedOutFlag();

  const { data, isLoading, isError, isFetching } = useModuleConfig(applicationId, {
    enabled: shouldFetchModuleConfig,
  });
  const baseConfig = loadSharedDashboardConfig();

  const waitingForSession =
    !onAuthCallback && Boolean(token) && !session;

  const waitingForModuleConfig =
    !onAuthCallback &&
    shouldFetchModuleConfig &&
    !isError &&
    !data &&
    (isLoading || isFetching);

  if (waitingForSession || waitingForModuleConfig) {
    return <AuthTransitionScreen />;
  }

  const modules = data?.modules;

  return (
    <ModuleConfigProvider
      modules={modules}
      version={data?.version ?? 0}
      isLoading={shouldFetchModuleConfig && isLoading}
    >
      <SharedDashboardProvider
        config={{
          ...baseConfig,
          modulePermissions: modules,
        }}
      >
        <App />
      </SharedDashboardProvider>
    </ModuleConfigProvider>
  );
};

export default ModuleConfigBootstrap;
